import { Params, useParams, useNavigate } from 'react-router-dom';
import { useGetCustomerById } from '@utils/api/back-office/customer-bo/use-get-customer-bo.tsx';
import NavigationCard from '@components/atoms/ui-kit/navigation-card.tsx';
import PageHeader from '@components/layout/page-header';
import { PATHS } from '../../router/paths';
import {
  User,
  Key,
  CreditCard,
  FileText,
  Users,
  Activity,
  Webhook,
  Palette,
  AlertTriangle,
  Bot
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function CustomerBoDetails() {
  const { customerId } = useParams<Params>();
  const navigate = useNavigate();
  const { data } = useGetCustomerById(Number(customerId));
  const { t } = useTranslation();

  const customerName = data?.result?.name || t('customer');

  const cards = [
    {
      title: t('bo-customer-profile-title'),
      description: t('bo-customer-profile-description'),
      details: [
        t('bo-customer-profile-detail-1'),
        t('bo-customer-profile-detail-2'),
        t('bo-customer-profile-detail-3'),
        t('bo-customer-profile-detail-4')
      ],
      icon: <User size={18} />,
      path: PATHS(customerId || '').CUSTOMER_PROFILE
    },
    {
      title: t('bo-customer-tokens-title'),
      description: t('bo-customer-tokens-description'),
      details: [
        t('bo-customer-tokens-detail-1'),
        t('bo-customer-tokens-detail-2'),
        t('bo-customer-tokens-detail-3'),
        t('bo-customer-tokens-detail-4')
      ],
      icon: <Key size={18} />,
      path: PATHS(customerId || '').CUSTOMER_TOKENS
    },
    {
      title: t('bo-customer-subscriptions-title'),
      description: t('bo-customer-subscriptions-description'),
      details: [
        t('bo-customer-subscriptions-detail-1'),
        t('bo-customer-subscriptions-detail-2'),
        t('bo-customer-subscriptions-detail-3'),
        t('bo-customer-subscriptions-detail-4')
      ],
      icon: <CreditCard size={18} />,
      path: PATHS(customerId || '').CUSTOMER_SUBSCRIPTIONS
    },
    {
      title: t('bo-customer-mandates-title'),
      description: t('bo-customer-mandates-description'),
      details: [
        t('bo-customer-mandates-detail-1'),
        t('bo-customer-mandates-detail-2'),
        t('bo-customer-mandates-detail-3'),
        t('bo-customer-mandates-detail-4')
      ],
      icon: <FileText size={18} />,
      path: PATHS(customerId || '').CUSTOMER_MANDATES
    },
    {
      title: t('bo-customer-iam-title'),
      description: t('bo-customer-iam-description'),
      details: [
        t('bo-customer-iam-detail-1'),
        t('bo-customer-iam-detail-2'),
        t('bo-customer-iam-detail-3'),
        t('bo-customer-iam-detail-4')
      ],
      icon: <Users size={18} />,
      path: PATHS(customerId || '').CUSTOMER_IAM
    },
    {
      title: t('bo-customer-activity-title'),
      description: t('bo-customer-activity-description'),
      details: [
        t('bo-customer-activity-detail-1'),
        t('bo-customer-activity-detail-2'),
        t('bo-customer-activity-detail-3'),
        t('bo-customer-activity-detail-4')
      ],
      icon: <Activity size={18} />,
      path: PATHS(customerId || '').CUSTOMER_ACTIVITY
    },
    {
      title: t('bo-customer-webhooks-title'),
      description: t('bo-customer-webhooks-description'),
      details: [
        t('bo-customer-webhooks-detail-1'),
        t('bo-customer-webhooks-detail-2'),
        t('bo-customer-webhooks-detail-3'),
        t('bo-customer-webhooks-detail-4')
      ],
      icon: <Webhook size={18} />,
      path: PATHS(customerId || '').CUSTOMER_WEBHOOKS
    },
    {
      title: t('bo-customer-templates-title'),
      description: t('bo-customer-templates-description'),
      details: [
        t('bo-customer-templates-detail-1'),
        t('bo-customer-templates-detail-2'),
        t('bo-customer-templates-detail-3'),
        t('bo-customer-templates-detail-4')
      ],
      icon: <Palette size={18} />,
      path: PATHS(customerId || '').CUSTOMER_TEMPLATES
    },
    {
      title: t('bo-customer-exceedances-title'),
      description: t('bo-customer-exceedances-description'),
      details: [
        t('bo-customer-exceedances-detail-1'),
        t('bo-customer-exceedances-detail-2'),
        t('bo-customer-exceedances-detail-3'),
        t('bo-customer-exceedances-detail-4')
      ],
      icon: <AlertTriangle size={18} />,
      path: PATHS(customerId || '').CUSTOMER_EXCEEDANCES
    },
    {
      title: t('bo-customer-ai-summary-title'),
      description: t('bo-customer-ai-summary-description'),
      details: [
        t('bo-customer-ai-summary-detail-1'),
        t('bo-customer-ai-summary-detail-2'),
        t('bo-customer-ai-summary-detail-3'),
        t('bo-customer-ai-summary-detail-4')
      ],
      icon: <Bot size={18} />,
      path: PATHS(customerId || '').CUSTOMER_AI_SUMMARY
    }
  ];

  return (
    <div className="bg-app flex-1">
      <PageHeader title={customerName} caption={t('bo-customer-subtitle')} />
      <div className="px-4 py-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
          {cards.map(card => (
            <NavigationCard
              key={card.title}
              title={card.title}
              description={card.description}
              details={card.details}
              icon={card.icon}
              onClick={() => navigate(card.path)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
